import { useState } from "react";

import styles from "./Settings.module.css";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Pill } from "../../components/Pill";
import {
  type ApiAppSession,
  type ApiSession,
  type AppPlatform,
  createApiToken,
  type CreatedApiToken,
  revokeApiToken,
  revokeAppSession,
  revokeSession,
  useApiTokens,
  useAppSessions,
  useSessions,
} from "../../data/api/account";
import {
  isPasskeyCancellation,
  passkeysSupported,
  registerPasskey,
  removePasskey,
  usePasskeys,
} from "../../data/api/webauthn";
import { Icon } from "../../icons";
import { relTime } from "../../lib/time";

const deviceLabel = (ua: string | null): string => {
  if (!ua) {
    return "Unknown device";
  }
  if (/curl/i.test(ua)) {
    return "Terminal (curl)";
  }
  const browser = /edg/i.test(ua)
    ? "Edge"
    : /chrome/i.test(ua)
      ? "Chrome"
      : /firefox/i.test(ua)
        ? "Firefox"
        : /safari/i.test(ua)
          ? "Safari"
          : "Browser";
  const os = /mac os/i.test(ua)
    ? "macOS"
    : /windows/i.test(ua)
      ? "Windows"
      : /linux/i.test(ua)
        ? "Linux"
        : /iphone|ipad/i.test(ua)
          ? "iOS"
          : /android/i.test(ua)
            ? "Android"
            : "";
  return os ? `${browser} on ${os}` : browser;
};

const ms = (iso: string | null): number => (iso ? Date.parse(iso) : Date.now());

// What a native client's `platform` renders as. Total over `AppPlatform`, the
// same shape `KIND_HELP` in AgentCredentials.tsx uses for the other closed
// server vocabulary: adding a platform is a typecheck failure here until it has
// a label, rather than a row rendering a raw `ipados` at somebody.
const PLATFORM_NAMES: Readonly<Record<AppPlatform, string>> = {
  android: "Android",
  ios: "iOS",
  ipados: "iPadOS",
  linux: "Linux",
  macos: "macOS",
  windows: "Windows",
};

/**
 * One row of the devices list, from either credential.
 *
 * The two are separate resources with separate revoke endpoints, and merging
 * them here rather than on the server is deliberate: what a person wants to see
 * is everywhere they are signed in, and what they need to press is one Revoke.
 * Each row carries the call that revokes it, so nothing has to guess which
 * store an id belongs to.
 */
type Device = {
  readonly current: boolean;
  readonly detail: string;
  readonly label: string;
  readonly lastSeen: number;
  readonly publicId: string;
  readonly revoke: (id: string) => Promise<unknown>;
};

const browserDevice = (session: ApiSession): Device => ({
  current: session.current,
  detail: session.remote_ip ?? "unknown IP",
  label: deviceLabel(session.user_agent),
  lastSeen: ms(session.last_seen_at ?? session.created_at),
  publicId: session.public_id,
  revoke: revokeSession,
});

const appDevice = (session: ApiAppSession): Device => ({
  current: session.current,
  // The app's own name and platform rather than a parsed user agent: a native
  // client says what it is, so there is nothing to infer.
  detail: `${PLATFORM_NAMES[session.client_platform]} · ${session.remote_ip ?? "unknown IP"}`,
  label: session.client_name,
  lastSeen: ms(session.last_seen_at ?? session.created_at),
  publicId: session.public_id,
  revoke: revokeAppSession,
});

export const Security = () => (
  <>
    <PasskeysSection />
    <SessionsSection />
    <TokensSection />
  </>
);

const PasskeysSection = () => {
  const { loading, passkeys, refetch } = usePasskeys();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const supported = passkeysSupported();

  const add = (): void => {
    if (busy || !supported) {
      return;
    }
    setBusy(true);
    setError(null);
    void registerPasskey("Passkey")
      .then(refetch)
      .catch((err: unknown) => {
        if (!isPasskeyCancellation(err)) {
          setError("Couldn't add that passkey. Try again.");
        }
      })
      .finally(() => setBusy(false));
  };

  const remove = (id: string): void => {
    void removePasskey(id)
      .then(refetch)
      .catch(() => undefined);
  };

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Passkeys</h3>
      <div className={styles.card}>
        <div className={styles.row}>
          <div className={styles.grow}>
            <div className={styles.help}>
              {supported
                ? "Sign in with Touch ID, Windows Hello, or a security key — no password needed."
                : "This browser doesn't support passkeys."}
            </div>
          </div>
          <Button
            disabled={busy || !supported}
            onClick={add}
            size="sm"
            variant="accent"
          >
            <Icon.Plus size={13} /> Add passkey
          </Button>
        </div>
        {error ? (
          <div className={styles.row}>
            <span className={styles.help}>⚠ {error}</span>
          </div>
        ) : null}
        {!loading && passkeys.length === 0 ? (
          <div className={styles.row}>
            <span className={styles.help}>No passkeys registered yet.</span>
          </div>
        ) : null}
        {passkeys.map((p) => (
          <div className={styles.row} key={p.public_id}>
            <div className={styles.grow}>
              <div className={styles.label}>{p.label}</div>
              <div className={styles.help}>
                {p.last_used_at
                  ? `last used ${relTime(ms(p.last_used_at))}`
                  : "never used"}{" "}
                · added {relTime(ms(p.created_at))}
              </div>
            </div>
            <Button
              onClick={() => remove(p.public_id)}
              size="sm"
              variant="ghost"
            >
              Remove
            </Button>
          </div>
        ))}
      </div>
    </section>
  );
};

const SessionsSection = () => {
  const { loading, refetch, sessions } = useSessions();
  const {
    appSessions,
    loading: loadingApps,
    refetch: refetchApps,
  } = useAppSessions();
  const [busy, setBusy] = useState<string | null>(null);

  const devices = [
    ...sessions.map(browserDevice),
    ...appSessions.map(appDevice),
  ].sort((a, b) => b.lastSeen - a.lastSeen);

  const revoke = (device: Device): void => {
    setBusy(device.publicId);
    void device
      .revoke(device.publicId)
      .then(() => {
        // Both, because the row that went is only in one of them and the caller
        // no longer knows which by the time this runs.
        refetch();
        refetchApps();
      })
      .catch(() => undefined)
      .finally(() => setBusy(null));
  };

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Active sessions</h3>
      <div className={styles.card}>
        {(loading || loadingApps) && devices.length === 0 ? (
          <div className={styles.row}>
            <span className={styles.help}>Loading sessions…</span>
          </div>
        ) : null}
        {devices.map((d) => (
          <div className={styles.row} key={d.publicId}>
            <div className={styles.grow}>
              <div className={styles.label}>
                {d.label}{" "}
                {d.current ? <Pill status="done">This device</Pill> : null}
              </div>
              <div className={styles.help}>
                {d.detail} · last active {relTime(d.lastSeen)}
              </div>
            </div>
            {d.current ? null : (
              <Button
                disabled={busy === d.publicId}
                onClick={() => revoke(d)}
                size="sm"
                variant="ghost"
              >
                Revoke
              </Button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

const TokensSection = () => {
  const { refetch, tokens } = useApiTokens();
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState<CreatedApiToken | null>(null);
  const [copied, setCopied] = useState(false);

  const create = (): void => {
    if (!name.trim() || creating) {
      return;
    }
    setCreating(true);
    void createApiToken(name.trim())
      .then((t) => {
        setRevealed(t);
        setName("");
        refetch();
      })
      .catch(() => undefined)
      .finally(() => setCreating(false));
  };

  const copy = (): void => {
    if (revealed) {
      void navigator.clipboard?.writeText(revealed.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    }
  };

  const revoke = (id: string): void => {
    void revokeApiToken(id)
      .then(refetch)
      .catch(() => undefined);
  };

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>API tokens</h3>
      <div className={styles.card}>
        {revealed ? (
          <div className={styles.tokenReveal}>
            <div className={styles.help}>
              Copy this token now — it won&apos;t be shown again.
            </div>
            <div className={styles.tokenRow}>
              <code className={styles.tokenValue}>{revealed.token}</code>
              <Button
                icon
                onClick={copy}
                size="sm"
                title="Copy"
                variant="ghost"
              >
                {copied ? <Icon.Check size={13} /> : <Icon.Copy size={13} />}
              </Button>
              <Button
                onClick={() => setRevealed(null)}
                size="sm"
                variant="ghost"
              >
                Dismiss
              </Button>
            </div>
          </div>
        ) : null}
        <div className={styles.row}>
          <Input
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                create();
              }
            }}
            placeholder="Token name (e.g. CI deploy)"
            style={{ flex: 1 }}
            value={name}
          />
          <Button
            disabled={!name.trim() || creating}
            onClick={create}
            size="sm"
            variant="accent"
          >
            <Icon.Plus size={13} /> Create token
          </Button>
        </div>
        {tokens.map((t) => (
          <div className={styles.row} key={t.public_id}>
            <div className={styles.grow}>
              <div className={styles.label}>{t.name}</div>
              <div className={styles.help}>
                <span className="mono">{t.token_prefix}…</span> ·{" "}
                {t.last_used_at
                  ? `last used ${relTime(ms(t.last_used_at))}`
                  : "never used"}{" "}
                · created {relTime(ms(t.created_at))}
              </div>
            </div>
            <Button
              onClick={() => revoke(t.public_id)}
              size="sm"
              variant="ghost"
            >
              Revoke
            </Button>
          </div>
        ))}
      </div>
    </section>
  );
};
